import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { TranslationsKeys } from '../../../locales/translations';
import { useRequirementFiltersData } from '../loaders/RequirementFiltersLoader';
import { useRequirementStudentsLoader } from '../loaders/RequirementStudentsLoader';
import { RequirementResultStatus } from '../types';

interface FiltersForm {
  idType?: SelectOptionType | null;
  idTypeCountry?: SelectOptionType | null;
  id?: string;
  name?: string;
  studyPlanVersion?: SelectOptionType | null;
  graduationPeriod?: SelectOptionType | null;
  status?: SelectOptionType | null;
}

export default function RequirementFilters({
  typeId,
  hideStatusFilter = false,
}: {
  typeId: number;
  hideStatusFilter?: boolean;
}) {
  const { t } = useTranslation(TranslationsKeys.GRADUATION_REQUIREMENTS_REVIEW);
  const data = useRequirementFiltersData();
  const { query, updateQuery } = useRequirementStudentsLoader();

  const { control, handleSubmit, reset, watch } = useForm<FiltersForm>({
    defaultValues: {
      idType: null,
      idTypeCountry: null,
      id: '',
      name: '',
      studyPlanVersion: null,
      graduationPeriod: null,
      status: null,
    },
  });

  const currentIdTypeOption = watch('idType');

  const currentIdType = useMemo(() => {
    return data.idTypes.find(
      (idType) => idType.id === currentIdTypeOption?.value,
    );
  }, [data, currentIdTypeOption]);

  const requiresIdTypeCountry = useMemo(() => {
    return !!currentIdType && !currentIdType?.countryId;
  }, [currentIdType]);

  const handleSearch = useCallback(
    (values: FiltersForm) => {
      updateQuery({
        ...query,
        typeId,
        page: 1,
        idType: (values.idType?.value as string) || undefined,
        idTypeCountry: (values.idTypeCountry?.value as string) || undefined,
        id: values.id || undefined,
        name: values.name || undefined,
        studyPlanVersionId: values.studyPlanVersion?.value
          ? Number(values.studyPlanVersion.value)
          : undefined,
        graduationPeriodId: values.graduationPeriod?.value
          ? Number(values.graduationPeriod.value)
          : undefined,
        status: !hideStatusFilter
          ? (values.status?.value as RequirementResultStatus) || undefined
          : undefined,
      });
    },
    [query, updateQuery, typeId, hideStatusFilter],
  );

  const handleClear = useCallback(() => {
    reset({
      idType: null,
      id: '',
      name: '',
      studyPlanVersion: null,
      graduationPeriod: null,
      status: null,
    });
    updateQuery({
      typeId,
      page: 1,
      items_per_page: query.items_per_page,
      enrollmentStatus: query.enrollmentStatus,
    });
  }, [
    reset,
    updateQuery,
    typeId,
    query.enrollmentStatus,
    query.items_per_page,
  ]);

  return (
    <Form onSubmit={handleSubmit(handleSearch)}>
      <Row className="pt-4 gutters-filters">
        <Col xs={12} md={3}>
          <OutlinedSelect
            control={control}
            name="idType"
            label={t('filters.idType.label')}
            placeholder={t('filters.idType.placeholder')}
            options={data.idTypes.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        {!!requiresIdTypeCountry && (
          <Col xs={12} md={3}>
            <OutlinedSelect
              control={control}
              name="idTypeCountry"
              label={t('filters.idTypeCountry.label', currentIdType)}
              placeholder={t('filters.idTypeCountry.placeholder')}
              options={data.countries.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </Col>
        )}
        <Col xs={12} md={3}>
          <TextOutlinedInput
            control={control}
            name="id"
            label={
              currentIdType
                ? t('filters.idDefined.label', currentIdType)
                : t('filters.id.label')
            }
            placeholder={
              currentIdType
                ? t('filters.idDefined.placeholder', currentIdType)
                : t('filters.id.placeholder')
            }
          />
        </Col>
        <Col xs={12} md={3}>
          <TextOutlinedInput
            control={control}
            name="name"
            label={t('filters.name.label')}
            placeholder={t('filters.name.placeholder')}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            control={control}
            name="studyPlanVersion"
            label={t('filters.studyPlan.label')}
            placeholder={t('filters.studyPlan.placeholder')}
            options={data.studyPlanVersions.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            control={control}
            name="graduationPeriod"
            label={t('filters.graduationPeriod.label')}
            placeholder={t('filters.graduationPeriod.placeholder')}
            options={data.graduationPeriods.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        {!hideStatusFilter && (
          <Col xs={12} md={3}>
            <OutlinedSelect
              control={control}
              name="status"
              label={t('filters.status.label')}
              placeholder={t('filters.status.placeholder')}
              options={[
                { value: 'pending', label: t('table.status.pending') },
                { value: 'approved', label: t('table.status.approved') },
                { value: 'rejected', label: t('table.status.rejected') },
              ]}
            />
          </Col>
        )}
        <Col
          xs={12}
          md={(hideStatusFilter ? 9 : 6) - (requiresIdTypeCountry ? 3 : 0)}
          className="d-flex align-items-end justify-content-end pb-2"
        >
          <Button text={t('common:actions.search')} size="md" type="submit" />
          <Button
            text={t('common:actions.clean')}
            className="ml-2"
            size="md"
            outlined
            type="button"
            onClick={handleClear}
          />
        </Col>
      </Row>
    </Form>
  );
}
